import * as React from 'react';
import MyTypes from 'MyTypes';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Modal, Table, Badge, Button, Spinner, Form, Row, Col } from 'react-bootstrap';
import { LuHistory, LuSearch, LuX } from 'react-icons/lu';
import Pager from 'components/common/Pager'; 
import * as models from '../../models'; 
import { contactsActions } from 'store/contacts';


interface Props extends PropsFromRedux {
    show: boolean;
    onHide: () => void;
    contactId: number;
}

interface State {
    searchModel: models.ContactSpecificAuditLogSearch
}

class AuditLogModal extends React.Component<Props, State> {

    getDefaultSearch = (): models.ContactSpecificAuditLogSearch => {
        return { 
            pageNo: 1, 
            pageSize: 20,
            contactId: this.props.contactId
        }
    }
    
    state: State = {
        searchModel: {...this.getDefaultSearch()} 
    };

    fetchData = () => {        
        if(this.state.searchModel.contactId != 0) {
            this.props.requestContactAudits(this.state.searchModel);         
        }
    }

    componentDidMount() {
        this.fetchData(); 
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.show && !prevProps.show) {
            this.setState({
                ...this.state,
                searchModel: this.getDefaultSearch()
            }, () => this.fetchData())            
        }
    }

    handlePageChange = (paging: models.Paging) => {  
        this.setState({
            searchModel: {
                ...this.state.searchModel, 
                ...paging,                
            }
        }, () => this.fetchData());
    }

    handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        this.fetchData(); 
    }

    handleReset = () => {
        this.setState({
            searchModel: {...this.getDefaultSearch()}
        }, () => this.props.requestContactAudits(this.getDefaultSearch()));
    }

    formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return (
            <div style={{lineHeight: '1.2'}}>
                <div className="fw-bold">{d.toLocaleDateString()}</div>
                <div className="text-muted small">{d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
            </div>
        );
    };

    renderValue = (val: any) => {
        if (val === null || val === undefined || val === '') {
            return <Badge bg="light" text="secondary" className="fw-normal border text-muted fst-italic">Empty</Badge>;
        }
        if (val === 'true' || val === true) return <Badge bg="success-subtle" text="success" className="border border-success">True</Badge>;
        if (val === 'false' || val === false) return <Badge bg="danger-subtle" text="danger" className="border border-danger">False</Badge>;
        
        return <span className="text-break">{String(val)}</span>;
    };

    render() {
        const { show, onHide, contactAudits, isContactAuditsLoading } = this.props;
        const { searchModel } = this.state;

        const showPagination = contactAudits?.totalCount > 20;

        return (
            <Modal show={show} onHide={onHide} size="xl" scrollable>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center">
                        <LuHistory className="me-2 text-primary" />
                        Audit History
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    
                    {/* --- SEARCH BAR --- */}
                    <div className="bg-light border-bottom p-3">
                        <Form onSubmit={this.handleSearch}>
                            <Row className="g-2">
                                <Col md={3}>
                                    <Form.Control 
                                        type="text" 
                                        placeholder="Filter by Field..." 
                                        size="sm"
                                        value={searchModel.fieldName || ''}
                                        onChange={(e) => this.setState({searchModel: {...searchModel, fieldName: e.target.value}})}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        size="sm" 
                                        value={searchModel.userContactId || ''}
                                        onChange={(e) => this.setState({searchModel: {...searchModel, userContactId: parseInt(e.target.value)}})}
                                    >
                                        <option value="">All Users</option>
                                        {contactAudits?.distinctUsers?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Control 
                                        type="date" 
                                        size="sm"
                                        value={searchModel?.startDate || ''}
                                        onChange={(e) => this.setState({searchModel: {...searchModel, startDate: e.target.value}})}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Control 
                                        type="date" 
                                        size="sm"
                                        value={searchModel?.endDate || ''}
                                        onChange={(e) => this.setState({searchModel: {...searchModel, endDate: e.target.value}})}
                                    />
                                </Col>
                                <Col md={2} className="d-flex gap-1">
                                    <Button type="submit" variant="primary" size="sm" className="w-100 d-flex align-items-center justify-content-center">
                                        <LuSearch className="me-1"/> Filter
                                    </Button>
                                    <Button type="button" variant="outline-secondary" size="sm" onClick={this.handleReset} title="Reset">
                                        <LuX />
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <div className="position-relative" style={{ minHeight: '350px' }}>
                        
                        {isContactAuditsLoading && (
                            <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white bg-opacity-75" style={{ zIndex: 5 }}>
                                <Spinner animation="border" variant="primary" />
                                <div className="text-muted mt-2 small">Searching logs...</div>
                            </div>
                        )}

                        <Table striped hover responsive className="mb-0 small align-middle">
                            <thead className="bg-light sticky-top border-bottom">
                                <tr>
                                    <th style={{ width: '15%' }}>Date & Time</th>
                                    <th style={{ width: '15%' }}>User</th>
                                    <th style={{ width: '15%' }}>Field</th>
                                    <th style={{ width: '27.5%' }} className="text-muted">Before</th>
                                    <th style={{ width: '27.5%' }}>After</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!isContactAuditsLoading && contactAudits?.totalCount === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center p-5 text-muted">
                                            <div className="fw-bold mb-1">No matches found</div>
                                            <div className="small">Try adjusting your filters</div>
                                        </td>
                                    </tr>
                                ) : (
                                    contactAudits?.resourceList?.map((log) => (
                                        <tr key={log.auditLogId}>
                                            <td>{this.formatDate(log.timestamp)}</td>
                                            <td className="fw-bold text-dark text-break" style={{maxWidth: '150px'}}>{log.userName}</td>
                                            <td><Badge bg="secondary" className="fw-normal">{log.fieldName}</Badge></td>
                                            <td className="text-muted border-end" style={{maxWidth: '200px'}}>
                                                {this.renderValue(log.oldValue)}
                                            </td>
                                            <td className="fw-bold" style={{maxWidth: '200px'}}>
                                                {this.renderValue(log.newValue)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                
                <Modal.Footer className="bg-light d-block p-2"> 
                    {showPagination && (
                        <Pager
                            className="admin-pager w-100 mb-0" 
                            retrieveDataCallback={this.handlePageChange}
                            pageSize={contactAudits?.pageSize || 1}
                            currentPage={contactAudits?.pageNo || 20}
                            totalCount={contactAudits?.totalCount}
                            pagesInRange={3}
                        />
                    )}
                </Modal.Footer>
            </Modal>
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    contactAudits: state.contacts.contacts.contactAuditLogs, 
    isContactAuditsLoading: state.contacts.contacts.isContactAuditsLoading,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {            
            requestContactAudits: contactsActions.requestContactAuditLogs, 
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(AuditLogModal);